import { useState } from 'react';
import { requestFundingApproval } from '../services/agreements';
import { readInvoiceRejection } from '../services/invoiceRejection';
import { fundingRequestFailure, SELECTION_CHANGED_REASON } from '../utils/fundingApprovalCopy';
import { fundingSelectionKey, normaliseCode } from '../utils/fundingCode';
import { approvalScopeKey, useApprovalScope } from './useApprovalScope';
import { useFundingApproverForm } from './useFundingApproverForm';
import type { FundingApproverForm } from './useFundingApproverForm';
import type { FundingPurpose, InvoiceLinePayload, PendingFundingApproval, RejectionRecorder } from '../types/agreements.types';

interface Options {
    agreementId: string | undefined;
    purpose: FundingPurpose;
    lines: InvoiceLinePayload[];
    now?: () => Date;
    beginRejection?: () => RejectionRecorder;
}

export interface FundingDelegation {
    pending: PendingFundingApproval | null;
    code: string;
    setCode: (raw: string) => void;
    approverForm: FundingApproverForm;
    emailOpen: boolean;
    reopenEmail: () => void;
    resend: () => Promise<void>;
    sending: boolean;
    resentAt: Date | null;
    requestError: string | null;
    notice: string | null;
    selectionChanged: boolean;
}

interface DelegationState {
    token: number;
    pending: PendingFundingApproval | null;
    selectionKey: string | null;
    code: string;
    selectionChanged: boolean;
    requestError: string | null;
    reopened: boolean;
    resentAt: Date | null;
}

const freshState = (token: number): DelegationState => ({
    token,
    pending: null,
    selectionKey: null,
    code: '',
    selectionChanged: false,
    requestError: null,
    reopened: false,
    resentAt: null,
});

/** Bound to the agreement, purpose and selection the approval was requested for and discarded
 *  during render when any changes. Component state only, never persisted or logged. */
export function useFundingDelegation({
    agreementId,
    purpose,
    lines,
    now = () => new Date(),
    beginRejection,
}: Options): FundingDelegation {
    const selectionKey = fundingSelectionKey(lines);
    const scope = useApprovalScope(approvalScopeKey(agreementId, purpose), selectionKey);
    const [state, setState] = useState<DelegationState>(() => freshState(scope.token));

    let current = state;
    if (state.token !== scope.token) {
        current = freshState(scope.token);
        setState(current);
    } else if (state.pending && state.selectionKey !== selectionKey) {
        scope.reset();
        current = { ...freshState(scope.token + 1), selectionChanged: selectionKey !== '' };
        setState(current);
    }
    const token = current.token;

    const exclusive = async (run: () => Promise<string | null>): Promise<string | null> => {
        if (!scope.lock.acquire(token)) return null;
        try {
            return await run();
        } finally {
            scope.lock.release(token);
        }
    };

    const request = async (approverEmail: string, isResend: boolean): Promise<string | null> => {
        if (!agreementId) return null;
        const requestedSelection = selectionKey;
        const recordRejection = beginRejection?.();
        const inScope = (previous: DelegationState) => previous.token === token;
        setState((previous) => (inScope(previous) ? { ...previous, requestError: null } : previous));
        try {
            const pending = await requestFundingApproval(
                agreementId,
                purpose,
                lines,
                approverEmail
            );
            const unchanged = scope.isCurrent(token, requestedSelection);
            setState((previous) =>
                !inScope(previous)
                    ? previous
                    : unchanged
                      ? {
                            ...freshState(token),
                            pending,
                            selectionKey: requestedSelection,
                            resentAt: isResend ? now() : null,
                        }
                      : { ...freshState(token), selectionChanged: true },
            );
            return null;
        } catch (error) {
            recordRejection?.(agreementId, readInvoiceRejection(error));
            if (!scope.isCurrent(token, requestedSelection)) return null;
            const failure = fundingRequestFailure(error);
            const notice = isResend ? (failure.fieldMessage ?? failure.notice) : failure.notice;
            setState((previous) => (inScope(previous) ? { ...previous, requestError: notice } : previous));
            return isResend ? null : failure.fieldMessage;
        }
    };

    const resend = async (): Promise<void> => {
        const pending = current.pending;
        if (!pending) return;
        await exclusive(() => request(pending.approverEmail, true));
    };

    const approverForm = useFundingApproverForm(
        (approverEmail) => exclusive(() => request(approverEmail, false)),
        scope.lock.locked,
        token,
    );

    return {
        pending: current.pending,
        code: current.code,
        setCode: (raw) => setState({ ...current, code: normaliseCode(raw) }),
        approverForm,
        emailOpen: current.pending === null || current.reopened,
        reopenEmail: () => setState({ ...current, reopened: true }),
        resend,
        sending: scope.lock.locked || approverForm.isSubmitting,
        resentAt: current.resentAt,
        requestError: current.requestError,
        notice: current.requestError ?? (current.selectionChanged ? SELECTION_CHANGED_REASON : null),
        selectionChanged: current.selectionChanged,
    };
}
